import { DateTime } from 'luxon';
import * as React from 'react';
import DropdownTreeSelect from 'react-dropdown-tree-select';
import InputMask from 'react-input-mask';
import "react-widgets/styles.css";
import { Button, Col, Collapse, FormFeedback, FormGroup, Input, Label, Row, Table, Toast, ToastBody, ToastHeader } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import { Afr, getSelectedAfr } from '../../redux/AfrSlice';
import { Auditor, AuditorDetailsInput, AuditorReference, getNewAuditorMessageEvents, loadAuditor, selectAuditor, selectPendingAuditors } from '../../redux/AuditorSlice';
import { getAllAuditorsAsync, selectAuditors } from '../../redux/AuditorsSlice';
import { selectedEntity } from '../../redux/EntitySlice';
import { AuditorRequestStatus, MessageEvent as MEvent, requestCPAFirm } from '../../redux/EventsSlice';
import phoneHelper from '../../utils/phoneHelper';
import utilityHelper, { StyledSelectableListItem } from '../../utils/utilityHelper';
import AddressEditorWithValidation from './AddressEditorWithValidation';


const AuditorRequestManager = (props: any) => {

    const dispatch = useAppDispatch();

    //selectors
    const entitySelector = useAppSelector(selectedEntity);
    const currentAfr: Afr | null = useAppSelector(getSelectedAfr);
    const pendingAuditorsSelector: Array<MEvent> | null = useAppSelector(selectPendingAuditors);
    const auditorSelector: Auditor | null = useAppSelector(selectAuditor);
    const existingAuditorsSelector = useAppSelector(selectAuditors);

    //constants


    //local hooks

    const [editMode, setEditMode] = React.useState<"NEW_AUDITOR" | "EDIT_AUDITOR" | "DISPLAY_AUDITOR_REQUESTS">
        (props.mode ? props.mode : "DISPLAY_AUDITOR_REQUESTS");


    const [auditorName, setAuditorName] = React.useState<string>('');

    //address hooks
    const [street, setStreet] = React.useState<string | null>(null);
    const [zipCode, setZipCode] = React.useState<string | null>(null);
    const [pobox, setPobox] = React.useState<string | null>(null);
    const [state, setState] = React.useState<string | null>(null);
    const [county, setCounty] = React.useState<string | null>(null);
    const [cityCode, setCityCode] = React.useState<string | null>(null);

    //contact info hooks
    const [phoneNumber, setPhoneNumber] = React.useState<string>("");
    const [formattedPhoneNumber, setFormattedPhoneNumber] = React.useState<string>("");
    const [phoneExtension, setPhoneExtension] = React.useState<string>("");
    const [email, setEmail] = React.useState<string>("");

    const [auditorRequestHistory, setAuditorRequestHistory] = React.useState<Array<AuditorRequestStatus>>([]);
    const [selectedAuditorName, setSelectedAuditorName] = React.useState<string>('');
    const [selectedAuditorNodeTitle, setSelectedAuditorNodeTitle] = React.useState<string>('');
    const [existingAuditorOptionNodes, setExistingAuditorOptionNodes] = React.useState<Array<StyledSelectableListItem>>([]);

    //functions

    const handleSaveClick = async () => {
        var result: { item1: string; item2: AuditorDetailsInput; item3: number };
        let detail: AuditorDetailsInput =
        {
            code: '',
            name: auditorName,
            contactInfo: {
                phoneNumber: phoneNumber,
                email: email,
                extension: phoneExtension
            },
            address: {
                street: street ? street : '',
                zip: zipCode ? zipCode : '',
                pobox: pobox ? pobox : '',
                cityId: cityCode ? cityCode : '',

            }
        };

        result = {
            item1: entitySelector && entitySelector.code ? entitySelector.code : '',
            item2: detail,
            item3: currentAfr ? currentAfr.year : 0
        };

        await dispatch(requestCPAFirm(result));
        setEditMode('DISPLAY_AUDITOR_REQUESTS');
    }

    const clearFormData = () => {
        setAuditorName('');
        setStreet(null);
        setZipCode(null);
        setPobox(null);
        setState(null);
        setCounty(null);
        setCityCode(null);
        setPhoneNumber("");
        setPhoneExtension("");
        setEmail("");
    }
    const extractAuditorZip = function (body: string): string {
        var parts : string[] = body.split("\"zip\":\"");
        if (parts.length < 2) {
            parts = body.toLowerCase().split("zip: ");
            if (parts.length < 2) {
                return "";
            }
            return parts[1].trim();
        }
        var endQuote : number = parts[1].indexOf('"');
        if (endQuote <= 0) {
            return "";
        }
        return parts[1].substring(0, endQuote).trim();
    }
    const extractAuditorReference = function (subject: string, body : string): string {
        var parts: string[] = subject.split(':');
        if (parts.length < 2) {
            return parts[0] + extractAuditorZip(body);
        }
        return parts[1].replace("Approved", "").replace("Rejected", "").trim() + extractAuditorZip(body);
    }
    const isResponse = function (targetAuditor: string, candidateSubject: string, candidateBody : string): boolean {
        return extractAuditorReference(candidateSubject, candidateBody) === targetAuditor;
    }
    const getAuditorDisplayReference = function (auditor: any) {
        var displayRef = auditor.name;
        if (auditor.address && auditor.address.zip) {
            displayRef += " (" + auditor.address.zip + ")";
        }
        return displayRef;
    }
    const initAuditorRequestHistory = () => {
        if (pendingAuditorsSelector) {
            if (pendingAuditorsSelector.length > 0) {
                var result: Array<AuditorRequestStatus> = [];
                var processedRequests: any = {};
                var orderedAuditorsSelector: MEvent[] = [...pendingAuditorsSelector];
                orderedAuditorsSelector.sort((a: MEvent, b: MEvent) => a.timestamp.localeCompare(b.timestamp) * -1);
                for (var i = 0; i < orderedAuditorsSelector.length; i++) {
                    if (orderedAuditorsSelector[i]) {
                        var mevent: MEvent = orderedAuditorsSelector[i];
                        switch (mevent.role) {
                            case 'REQUEST_AUDITOR':
                                var targetAuditor = extractAuditorReference(mevent.message.subject, mevent.message.body);
                                if (processedRequests.hasOwnProperty(targetAuditor)) {
                                    continue;
                                }
                                processedRequests[targetAuditor] = true;
                                var response = pendingAuditorsSelector.find((element) => element.role != "REQUEST_AUDITOR"
                                    && element.timestamp.localeCompare(mevent.timestamp) > 0
                                    && isResponse(targetAuditor, element.message.subject, element.message.body));

                                var newAuditorRequest: AuditorRequestStatus = {
                                    status: response ? response.role : mevent.role,
                                    displayCode: '',
                                    auditor: JSON.parse(mevent.message.body),
                                    requested: mevent.timestamp,
                                    requestor: mevent.creator,
                                    response: response ? response.role : null,
                                    responded: response ? response.timestamp : null
                                };

                                result.push(newAuditorRequest);
                                break;
                            case 'AUDITOR_APPROVAL':
                                break;
                            case 'AUDITOR_REJECTION':
                                break;
                            default:
                                break;

                        }

                    }
                }
                if (result.length > 0) {
                    setAuditorRequestHistory(result);
                }
            }
            else {
                setAuditorRequestHistory([]);
            }
        }
        else {
            setAuditorRequestHistory([]);
        }
    }

    const resetModifyViewState = () => {
        setSelectedAuditorName('');
        setPhoneNumber('');
        setEmail('');
        setStreet(null);
        setZipCode(null);
        setPobox(null);
        setState(null);
        setCounty(null);
        setCityCode(null);
        dispatch(loadAuditor(null));
    }

    const initAuditorDetailFields = () => {
        setPhoneNumber("");
        setFormattedPhoneNumber("");
        setEmail("");
        if (auditorSelector) {
            setSelectedAuditorName(auditorSelector.name);
            if (auditorSelector.info) {
                if (auditorSelector.info.number) {
                    setPhoneNumber(auditorSelector.info.number.number);
                    setFormattedPhoneNumber(phoneHelper.formatPhoneNumber(auditorSelector.info.number.number) + (auditorSelector.info.number.extension ? ", ext: " + auditorSelector.info.number.extension : ""));
                }
                setEmail(auditorSelector.info.email);
            }
            setSelectedAuditorNodeTitle(auditorSelector.name);
            if (auditorSelector.address) {
                if (auditorSelector.address.street) {
                    setStreet(auditorSelector.address.street);
                }
                if (auditorSelector.address.zip) {
                    setZipCode(auditorSelector.address.zip);
                }
                if (auditorSelector.address.pobox) {
                    setPobox(auditorSelector.address.pobox);
                }
                if (auditorSelector.address.city &&
                    auditorSelector.address.city.code) {
                    setCityCode(auditorSelector.address.city.code);
                }
                setState(null);
                setCounty(null);
            }
            else {
                setStreet(null);
                setZipCode(null);
                setPobox(null);
                setState(null);
                setCounty(null);
                setCityCode(null);
            }
        }
    }

    const onExistingAuditorOptionChange = (currentNode: any, selectedNodes: any): void => {

        var foundCurrentNodeIndex = existingAuditorOptionNodes.findIndex((element) => element.value == currentNode.value);
        if (foundCurrentNodeIndex) {
            if (selectedNodes.length > 0) {

                dispatch(loadAuditor(currentNode.value));


                //update selected values to be false
                existingAuditorOptionNodes.forEach((element) => {
                    element.checked = false;
                    element.children.forEach((element) => { element.checked = false; })
                });

                //if single node
                if (existingAuditorOptionNodes[foundCurrentNodeIndex] && existingAuditorOptionNodes[foundCurrentNodeIndex].disabled == false) {
                    existingAuditorOptionNodes[foundCurrentNodeIndex].checked = true;
                }
                //if parent node with children
                else {
                    //find parent node index
                    var foundParentIndex = existingAuditorOptionNodes.findIndex((x) => x.children.length > 0
                        && x.children.find((y) => y.value == currentNode.value) != undefined
                    );
                    //find child node index
                    var foundChildIndex = existingAuditorOptionNodes[foundParentIndex].children.findIndex((x) => x.value == currentNode.value);
                    existingAuditorOptionNodes[foundParentIndex].children[foundChildIndex].checked = true;
                }
            }
            else {

                //update selected values to be false
                existingAuditorOptionNodes.forEach((element) => {
                    element.checked = false;
                    element.children.forEach((element) => { element.checked = false; })
                });
                dispatch(loadAuditor(null));
            }
        }

    }

    const handleUpdateAuditorClick = async (e: any): Promise<void> => {

        var result: { item1: string; item2: AuditorDetailsInput; item3: number };

        //update selected values to be false
        existingAuditorOptionNodes.forEach((element) => {
            element.checked = false;
            element.children.forEach((element) => { element.checked = false; })
        });

        let detail: AuditorDetailsInput =
        {
            code: auditorSelector !== null ? auditorSelector.code : "",
            name: selectedAuditorName,
            contactInfo: {
                phoneNumber: phoneNumber,
                email: email,
                extension: phoneExtension
            },
            address: {
                cityId: cityCode ? cityCode : '',
                pobox: pobox ? pobox : '',
                street: street ? street : '',
                zip: zipCode ? zipCode : ''
            },
        };

        result = {
            item1: entitySelector && entitySelector.code ? entitySelector.code : '',
            item2: detail,
            item3: currentAfr ? currentAfr.year : 0
        };

        await dispatch(requestCPAFirm(result));
        setEditMode('DISPLAY_AUDITOR_REQUESTS');

    }

    const initExistingAuditorListTree = (existingAuditors: Array<AuditorReference> | null, setList: any) => {

        if (existingAuditors) {
            let result: Array<StyledSelectableListItem> = [];
            let index: number = 0;

            for (var i = 0; i < existingAuditors.length; i++) {

                index = result.findIndex((element) => element.label == existingAuditors[i].name);

                if (index === -1) {
                    //parent node, create parent
                    result.push({
                        value: existingAuditors[i].code,
                        label: existingAuditors[i].name,
                        children: [],
                        checked: false,
                        disabled: true,
                        className: ""
                    })

                    index = result.findIndex((element) => element.label == existingAuditors[i].name);

                    result[index].children.push({
                        value: existingAuditors[i].code,
                        label: result[index].label + " Zip Code [" + existingAuditors[i].branchId + "]",
                        children: [],
                        checked: false,
                        disabled: false,
                        className: ""
                    });
                }
                else {
                    result[index].children.push({
                        value: existingAuditors[i].code,
                        label: result[index].label + " Zip Code [" + existingAuditors[i].branchId + "]",
                        children: [],
                        checked: false,
                        disabled: false,
                        className: ""
                    });
                }
            }

            result.forEach(element => {
                if (element.children.length == 1) {
                    element.children = [];
                    element.disabled = false;
                }
            })
            setList(result);
        }
    }

    function isCreateAuditorFormValid(): boolean | undefined {
        return utilityHelper.isTextPresent(email) == true
            && utilityHelper.isTextPresent(auditorName) == true
            && utilityHelper.isTextPresent(phoneNumber) == true
            && (utilityHelper.isTextPresent(street) == true || utilityHelper.isTextPresent(pobox) == true)
            && (cityCode && cityCode != "fl_(selectacity)" && cityCode != "") == true
            && utilityHelper.isTextPresent(zipCode) == true
            ;

    }

    function isUpdateAuditorFormValid(): boolean | undefined {
        return utilityHelper.isTextPresent(email) == true
            && utilityHelper.isTextPresent(selectedAuditorName) == true
            && utilityHelper.isTextPresent(phoneNumber) == true
            && (utilityHelper.isTextPresent(street) == true || utilityHelper.isTextPresent(pobox) == true)
            && (cityCode && cityCode != "fl_(selectacity)" && cityCode != "") == true
            && utilityHelper.isTextPresent(zipCode) == true
            ;

    }


    //side effects
    React.useEffect(() => {
        initAuditorRequestHistory();
    }, [pendingAuditorsSelector])


    React.useEffect(() => {
        if (auditorSelector) {
            initAuditorDetailFields();
        } else {
            setFormattedPhoneNumber("");
        }
    }, [auditorSelector])

    React.useEffect(() => {
        if (!existingAuditorsSelector || existingAuditorsSelector.length <= 0) {
            dispatch(getAllAuditorsAsync());
        }
    }, [existingAuditorsSelector])

    React.useEffect(() => {
        if (editMode == 'NEW_AUDITOR') {
            clearFormData();
        }
        else if (editMode == 'DISPLAY_AUDITOR_REQUESTS') {
            if (currentAfr && currentAfr.afrId) {
                dispatch(getNewAuditorMessageEvents(currentAfr.afrId));
            }
        }
        else if (editMode == 'EDIT_AUDITOR') {

            initExistingAuditorListTree(existingAuditorsSelector, setExistingAuditorOptionNodes);
        }

    }, [editMode])

    //JSX templates
    const createModeTemplate = (<React.Fragment>
        <Row style={{ marginBottom: "2em" }}>
            <Col>
                <h3>New Auditor Request</h3>
            </Col>
        </Row>
        <Row>
            <Col>
                <Row>
                    <Col>
                        <FormGroup>
                            <Row>
                                <Col>
                                    <Label>Auditor Name:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="text"
                                        value={auditorName}
                                        onChange={(e) => setAuditorName(e.currentTarget.value)}
                                        valid={utilityHelper.isTextPresent(auditorName)}
                                        invalid={!utilityHelper.isTextPresent(auditorName)} />
                                    <FormFeedback>Auditor name is required.</FormFeedback>
                                </Col>
                            </Row>
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <FormGroup>
                            <Row>
                                <Col>
                                    <Label>Email:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.currentTarget.value)}
                                        valid={utilityHelper.isTextPresent(email)}
                                        invalid={!utilityHelper.isTextPresent(email)} />
                                    <FormFeedback>Email is required.</FormFeedback>
                                </Col>
                            </Row>
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <FormGroup>
                            <Row>
                                <Col >
                                    <Label>Phone Number:</Label>
                                </Col>
                                <Col md="4">
                                    <Input
                                        type="tel"
                                        mask="(999) 999-9999"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.currentTarget.value)}
                                        tag={InputMask}
                                        valid={utilityHelper.isTextPresent(phoneNumber) == true}
                                        invalid={utilityHelper.isTextPresent(phoneNumber) == false }
                                    />
                                </Col>
                                <Col>
                                    <Label>Extension:</Label>
                                </Col>
                                <Col md="2">
                                    <Input
                                        value={phoneExtension}
                                        onChange={(e) => setPhoneExtension(e.currentTarget.value)}
                                    />
                                </Col>
                            </Row>
                        </FormGroup>
                    </Col>
                </Row>
                <Row style={{ marginTop: "1em" }}>
                    <Col>
                        <FormGroup>
                            <Row>
                                <Col >
                                    <Button disabled={isCreateAuditorFormValid() == false} onClick={handleSaveClick}>Create Auditor Request</Button>
                                </Col>
                                <Col >
                                    <Button
                                        onClick={() => { setEditMode('DISPLAY_AUDITOR_REQUESTS'); }}>
                                        Cancel Auditor Request
                                    </Button>
                                </Col>
                            </Row>
                        </FormGroup>
                    </Col>
                </Row>
            </Col>

            <Col>
                <FormGroup>
                    <Row>
                        <Col>
                            <AddressEditorWithValidation
                                street={street}
                                setStreet={setStreet}
                                zipCode={zipCode}
                                setZipCode={setZipCode}
                                poBox={pobox}
                                setPoBox={setPobox}
                                cityCode={cityCode}
                                setCityCode={setCityCode}
                                county={county}
                                setCounty={setCounty}
                                state={state}
                                setState={setState}
                                canEdit={true}
                                streetOrPOIsValid={utilityHelper.isTextPresent(street) == true || utilityHelper.isTextPresent(pobox) == true}
                            />
                        </Col>
                    </Row>
                </FormGroup>
            </Col>
        </Row>
    </React.Fragment>)

    const displayModeTemplate = (<React.Fragment>
        <Row style={{ marginBottom: "1em" }}>
            <Col>
                <h3>Auditor Requests</h3>
            </Col>
        </Row>
        <Row >
            <Col>
                <Label style={{ margin: "1rem" }}>
                    This section is for requesting an auditor firm to be input into the system or
                    to update information for an existing firm. To allow an individual auditor log-in
                    access to your AFR, the Primary Editor must input their information in the Entity
                    Management tile. Please see pages 6-7 of the <a href="https://myfloridacfo.com/division/aa/local-governments"
                        target="_blank" style={{ textDecoration: "underline", color: "blue" }}>LOGERX Manual</a> for instructions.
                </Label>
            </Col>
        </Row>
        <Row>
            <Col>
                <Button style={{ margin: "1rem", textAlign:"left", width:"13em" }} onClick={() => { setEditMode('NEW_AUDITOR') }} >Request New Auditor</Button>
                <br />
                <br />
                <Button style={{ margin: "1rem", textAlign: "left", width: "13em" }} onClick={() => { setEditMode('EDIT_AUDITOR') }}>Request Auditor Update</Button>
            </Col>
            <Col>
                <Toast className="auditDetail">
                    <ToastHeader className="auditDetail">All Auditor Requests</ToastHeader>
                    <ToastBody>
                        <div className="scrollable-table">
                            <Table className="table" >
                                <thead className="scrollable-table-header thead-dark">
                                    <tr>
                                        <th className="col-3">Auditor Name</th>
                                        <th className="col-1">Requested</th>
                                        <th className="col-1">Responded</th>
                                        <th className="col-2">Status</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {auditorRequestHistory.map((request) => (
                                        <tr>
                                            <td>{getAuditorDisplayReference(request.auditor)}</td>
                                            <td>{DateTime.fromISO(request.requested).toFormat('LL/dd/yyyy')}</td>
                                            <td>{request.responded ? DateTime.fromISO(request.responded).toFormat('LL/dd/yyyy') : ""}</td>
                                            <td>{utilityHelper.SnakeCaseToTitleCase(request.status)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                    </ToastBody>
                </Toast>
            </Col>
        </Row>
    </React.Fragment>);

    const editModeTemplate = (<React.Fragment>
        <Row style={{ marginBottom: "1em" }}>
            <Col md="5">
                <h4>Update Existing Auditor Request</h4>
            </Col>
            <Col md="7" hidden={!auditorSelector}>
                <h4>Details For {selectedAuditorNodeTitle}</h4>
            </Col>
        </Row>
        <Row>
            <Col md="5">
                <Row>
                    <Col>
                        <DropdownTreeSelect
                            id="existing"
                            data={existingAuditorOptionNodes}
                            onChange={onExistingAuditorOptionChange}
                            mode={"radioSelect"}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col></Col>
                </Row>
                <Collapse isOpen={auditorSelector != null}>
                    <Row style={{ marginTop: "5em" }}>
                        <Col>
                            <Button disabled={isUpdateAuditorFormValid() == false} onClick={(e) => { handleUpdateAuditorClick(e) }}>Update Auditor</Button>
                        </Col>
                        <Col><Button onClick={() => { resetModifyViewState() }} >Cancel</Button></Col>
                    </Row>
                </Collapse>
            </Col>
            <Col md="7">
                {auditorSelector ? (
                    <React.Fragment>
                        <Row>
                            <Col>
                                <Row>
                                    <Col>
                                        <FormGroup>
                                            <Row>
                                                <Col>
                                                    <Label>Audit Firm Name:</Label>
                                                </Col>
                                                <Col md="9">
                                                    <Input type="text"
                                                        value={selectedAuditorName}
                                                        onChange={(e) => { setSelectedAuditorName(e.currentTarget.value) }}
                                                        valid={utilityHelper.isTextPresent(selectedAuditorName) == true}
                                                        invalid={utilityHelper.isTextPresent(selectedAuditorName) == false}
                                                    />
                                                </Col>
                                            </Row>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <Row>
                                        <Col>
                                            <Label>Email:</Label>
                                        </Col>
                                        <Col md="9">
                                            <Input type="email"
                                                value={email || ''}
                                                onChange={(e) => setEmail(e.currentTarget.value)}
                                                valid={utilityHelper.isTextPresent(email)}
                                                invalid={!utilityHelper.isTextPresent(email)} />
                                            <FormFeedback>Email is required.</FormFeedback>
                                        </Col>
                                    </Row>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <Row>
                                        <Col >
                                            <Label>Phone Number:</Label>
                                        </Col>
                                        <Col md="9">
                                            <Input
                                                type="tel"
                                                mask="(999) 999-9999"
                                                value={phoneNumber}
                                                onChange={(e) => setPhoneNumber(e.currentTarget.value)}
                                                tag={InputMask}
                                                valid={utilityHelper.isTextPresent(phoneNumber) == true}
                                                invalid={utilityHelper.isTextPresent(phoneNumber) == false}
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Row>
                                    <Col>
                                        <AddressEditorWithValidation
                                            street={street}
                                            setStreet={setStreet}
                                            zipCode={zipCode}
                                            setZipCode={setZipCode}
                                            poBox={pobox}
                                            setPoBox={setPobox}
                                            cityCode={cityCode}
                                            setCityCode={setCityCode}
                                            county={county}
                                            setCounty={setCounty}
                                            state={state}
                                            setState={setState}
                                            canEdit={true}
                                            streetOrPOIsValid={utilityHelper.isTextPresent(street) == true || utilityHelper.isTextPresent(pobox) == true}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </React.Fragment>
                ) : (<div></div>)}

            </Col>
        </Row>

    </React.Fragment>)

    return (
        <React.Fragment>
            <Collapse isOpen={editMode == 'NEW_AUDITOR'}>
                {createModeTemplate}
            </Collapse>
            <Collapse isOpen={editMode == 'DISPLAY_AUDITOR_REQUESTS'}>
                {displayModeTemplate}
            </Collapse>
            <Collapse isOpen={editMode == 'EDIT_AUDITOR'}>
                {editModeTemplate}
            </Collapse>
        </React.Fragment>
    );
}

export default AuditorRequestManager;